import { posBOAccess } from "@/utils/posRoles";
import { AuthService } from "../resource";
import { lineLinkFields, isLineLinked } from "../utils/boLine";
import { normalizeSellerAccess, isSellerUser } from "../utils/sellerAccessBO";

const AuthModule = {
  namespaced: true,
  state: {
    datas: [],
    user: {
      id: "",
      fname: "",
      lname: "",
      nick_name: "",
      email: "",
      group: "",
      phone:"",
      roles:"",
      enterprise_id: "",
      enterprise_name: "",
      team_id: "",
      team_name: "",
	  sale_channel_id: "",
      sale_channel_name: "",
      // LINE account link (spec 2026-10-03 §2 #6). Empty string = not linked;
      // the login response does NOT carry these — GET /api/v1/profile does —
      // so they stay "" until the profile screen fetches them.
      line_user_id: "",
      line_display_name: "",
      line_picture_url: "",
      line_linked_at: "",
    },
	locale:"th"
  },
  mutations: {
	SET_LOCALE(state, data) {
		state.locale = data;
	  },
    SET_DATA(state, data) {
      state.datas = data;
    },
    SET_PROFILE(state, data) {
      state.user.fname = data.fname;
      state.user.lname = data.lname;
      state.user.nick_name = data.nick_name;
      state.user.phone = data.phone;
      // GET /api/v1/profile is the ONLY response that carries the LINE link
      // (spec §2 #6) — the login body does not. Normalised through
      // lineLinkFields so an API build without the columns reads as "not
      // linked" rather than writing `undefined` into the card.
      const line = lineLinkFields(data);
      state.user.line_user_id = line.line_user_id;
      state.user.line_display_name = line.line_display_name;
      state.user.line_picture_url = line.line_picture_url;
      state.user.line_linked_at = line.line_linked_at;
	},
    // SET_LINE_LINK is the narrow write used right after POST .../line/link
    // (body = the 4 fields) and after DELETE .../line (pass nothing / {} to
    // clear). It deliberately does NOT touch the rest of the profile, so an
    // unlink cannot blank out a name the user is editing in the form above.
    SET_LINE_LINK(state, data) {
      const line = lineLinkFields(data);
      state.user.line_user_id = line.line_user_id;
      state.user.line_display_name = line.line_display_name;
      state.user.line_picture_url = line.line_picture_url;
      state.user.line_linked_at = line.line_linked_at;
    },
    SET_USER(state, data) {
	  state.user = {}
      state.user.id = data.Id;
      state.user.fname = data.FirstName;
      state.user.lname = data.LastName;
      state.user.nick_name = data.NickName;
      state.user.phone = data.Phone;
      state.user.email = data.Email;
      state.user.group = data.GroupId;
      state.user.enterprise_id = data.EnterpriseID;
      state.user.enterprise_name = data.EnterpriseName;
      state.user.status = data.Status;
      state.user.team_id = data.TeamID;
      state.user.team_name = data.TeamName;
      state.user.sale_channel_id = data.SaleChannelID;
      state.user.sale_channel_name = data.SaleChannelName;
      state.user.roles = JSON.parse(data.Roles);
      // can_view_cost (#265 / bo-api #594) is NOT a role and is NOT in the JWT:
      // the server decides it per LOGIN from config.yml's product_cost.emails,
      // so it can be revoked by rolling config without waiting out a 24h token.
      // Missing/undefined must read as false — an older API that does not send
      // the flag has to mean "no cost", never "cost for everybody".
      state.user.can_view_cost = data.CanViewCost === true;
      // can_create_coupon_event (coupon-event spec §2) is the same kind of flag
      // as can_view_cost and for the same reason: the server derives it per
      // LOGIN from config.yml's coupon_event.emails, never from a JWT claim, so
      // revoking it is a config roll rather than a 24h wait. Missing/undefined
      // must read as false — an older API that does not send the flag means "no
      // one may create", never "everyone may".
      state.user.can_create_coupon_event = data.CanCreateCouponEvent === true;
      // seller_access (spec 2026-10-16 §6, bo-api #986) rides beside User in
      // the login body like can_view_cost. null = an older API without it →
      // Auth/isSeller falls back to roles.seller.
      state.user.seller_access = normalizeSellerAccess(data.SellerAccess);
      // SET_USER starts from `state.user = {}`, so the LINE fields have to be
      // re-seeded here or `line_user_id` would be `undefined` until the profile
      // screen fetches. The login body does not carry them (spec §2 #2 answers
      // the plain LoginResponse), so "" is the honest value.
      state.user.line_user_id = "";
      state.user.line_display_name = "";
      state.user.line_picture_url = "";
      state.user.line_linked_at = "";
    },
    CLEAR_USER(state, data) {
      state.user.id = "";
      state.user.fname = "";
      state.user.lname = "";
      state.user.nick_name = "";
      state.user.phone = "";
      state.user.email = "";
      state.user.group = "";
      state.user.status = "";
      state.user.team_id = "";
      state.user.team_name = "";
      state.user.sale_channel_id = "";
      state.user.sale_channel_name = "";
      state.user.roles = "";
      state.user.enterprise_id = "";
      state.user.enterprise_name = "";
      state.user.can_view_cost = false;
      state.user.can_create_coupon_event = false;
      state.user.seller_access = null;
      // A logout must drop the LINE identity too: leaving a picture + display
      // name behind would show the previous staff member's LINE account to
      // whoever logs in next on a shared packing-room machine.
      state.user.line_user_id = "";
      state.user.line_display_name = "";
      state.user.line_picture_url = "";
      state.user.line_linked_at = "";
    }
  },
  actions: {

    ForgotPassword({ commit, state }, data) {
      return AuthService.ForgotPassword(data);
    },
    ResetPassword({ commit, state }, data) {
      return AuthService.ResetPassword(data);
    },
    setUser({ commit, state }, data) {
      commit("SET_USER", data);
    },
	setProfile({ commit, state }, data) {
		console.log(data)
      commit("SET_PROFILE", data);
	},
    // --- LINE link / LINE login (spec 2026-10-03 §2) ----------------------
    // These stay thin pass-throughs on purpose: the decision of WHICH message
    // to show lives in src/utils/boLine.js where it can be unit-tested, and the
    // token hand-off has to happen in the component that owns the cookie and
    // the router.
    lineAuthorizeUrl({ commit, state }, purpose) {
      return AuthService.LineAuthorizeUrl(purpose);
    },
    lineCallback({ commit, state }, data) {
      return AuthService.LineCallback(data);
    },
    lineProfileAuthorizeUrl({ commit, state }) {
      return AuthService.LineProfileAuthorizeUrl();
    },
    lineLink({ commit, state }, data) {
      return AuthService.LineLink(data).then(res => {
        // #4 answers with the 4 columns it just wrote — commit them so the card
        // flips to "linked" without a second round trip.
        commit("SET_LINE_LINK", (res && res.data) || {});
        return res;
      });
    },
    lineUnlink({ commit, state }) {
      return AuthService.LineUnlink().then(res => {
        commit("SET_LINE_LINK", {});
        return res;
      });
    },
    setLineLink({ commit, state }, data) {
      commit("SET_LINE_LINK", data);
    },
    createData({ commit, state }, data) {
      return AuthService.AuthCreate(data);
    },
    updateData({ commit, state }, data) {
      return AuthService.AuthUpdate(data);
    },
    deleteData({ commit, state }, data) {
      return AuthService.AuthDelete(data);
    },
    getProfile({ commit, state }, data) {
      return AuthService.GetProfile(data);
    },
    changeProfile({ commit, state }, data) {
      return AuthService.ChangeProfile(data);
    },
    changePassword({ commit, state }, data) {
      return AuthService.ChangePassword(data);
    },
    async SignIn({ commit, state }, data) {
      return await AuthService.SignIn(data);
    },
    async listData({ commit, state }) {
      try {
        const data = await AuthService.AuthList();
        if (data.data.Data == null) {
          commit("SET_DATA", []);
        } else {
          commit("SET_DATA", data.data.Data);
        }
      } catch (err) {
        commit("SET_DATA", []);
      }
    },
    async signOut({ commit, state }) {
      try {
        const data = await AuthService.SignOut();
        commit("CLEAR_USER", state.user);
      } catch (err) {
        commit("CLEAR_USER", state.user);
      }
    },
	setLocale({ commit, state }, locale) {
        commit("SET_LOCALE", locale);
	}
  },
  getters: {
    getData(state) {
      return state.datas;
    },
	getLocale(state) {
		return state.locale;
	},
    getUser(state) {
      return state.user;
    },

	// LINE link state for the Edit Profile card (spec 2026-10-03 §5). Two
	// getters rather than reading state.user.line_user_id at the call site, so
	// the "is it linked" rule has exactly one definition (src/utils/boLine.js)
	// shared with the callback page.
	getLineLink(state) {
		return {
			line_user_id: state.user.line_user_id || "",
			line_display_name: state.user.line_display_name || "",
			line_picture_url: state.user.line_picture_url || "",
			line_linked_at: state.user.line_linked_at || ""
		};
	},

	isLineLinked(state) {
		return isLineLinked(state.user);
	},


    isAuthenticate(state) {
		try {
			return state.user.id != "";
		} catch {
			return false;
		}
    },

	isAccessWms(state) {
		return state.user.roles.wms > 0;
	},

	// Mirrors mw.IsFullWms on the API: the full WMS role (7) OR SuperAdmin.
	// isAccessWms (> 0) is too loose for WMS write endpoints - a partial role
	// would render a control that answers 403 access_denied (permission
	// dialog) when clicked.
	isFullWms(state) {
		try {
			return state.user.roles.wms == 7 || state.user.group == 99;
		} catch {
			return false;
		}
	},

	isAccessOrder(state) {
		return state.user.roles.order > 0;
	},

	// Mirror mw.IsFullOrder / mw.IsFullPayment (role 7 = All, or SuperAdmin).
	// Those gates answer a partial role with 403 access_denied (http.js shows a
	// permission dialog; 401 = logout is only for an invalid / expired token,
	// bo-api #937) — so a control behind them must not render for a partial
	// role (spec 2026-10-14 §5.3).
	isFullOrder(state) {
		try {
			return state.user.roles.order == 7 || state.user.group == 99;
		} catch {
			return false;
		}
	},
	isFullPayment(state) {
		try {
			return state.user.roles.payment == 7 || state.user.group == 99;
		} catch {
			return false;
		}
	},

	// ภาพรวมรายการสั่งซื้อ (owner 2026-10-11): its own right `order_overview`
	// (bo-api #878 gives Admin/Owner/SuperAdmin All by default). A login from an
	// API without the key keeps the old behaviour for ผู้ดูแลระบบ (groups 1/2/99).
	isAccessOrderOverview(state) {
		try {
			const r = state.user.roles || {};
			if (r.order_overview === undefined || r.order_overview === null) {
				const g = Number(state.user.group);
				return g === 1 || g === 2 || g === 99;
			}
			return Number(r.order_overview) > 0;
		} catch {
			return false;
		}
	},

	// การตลาด (Phase 1 ads §6.1): roles.marketing — 1 read (view the LP list /
	// editor read-only + the source report), >= 2 write. bo-api gives Admin /
	// Owner / SuperAdmin All by default (RoleByGroup); a login from an API
	// without the key falls back to the admin groups 1/2/99, like order_overview.
	isAccessMarketing(state) {
		try {
			const r = state.user.roles || {};
			if (r.marketing === undefined || r.marketing === null) {
				const g = Number(state.user.group);
				return g === 1 || g === 2 || g === 99;
			}
			return Number(r.marketing) > 0;
		} catch {
			return false;
		}
	},
	canWriteMarketing(state) {
		try {
			const g = Number(state.user.group);
			if (g === 99) return true;
			const r = state.user.roles || {};
			if (r.marketing === undefined || r.marketing === null) return g === 1 || g === 2;
			return Number(r.marketing) >= 2;
		} catch {
			return false;
		}
	},

	// การเงิน Affiliate (Phase 3 §3.1 / §5.1): roles.affiliate_finance — 1 read
	// (unmasked payout profiles, documents, payout rounds), >= 2 write (verify,
	// adjust, create / pay rounds). bo-api gives Admin / Owner / SuperAdmin All;
	// a login from an API without the key falls back to groups 1/2/99.
	isAccessAffiliateFinance(state) {
		try {
			const r = state.user.roles || {};
			if (r.affiliate_finance === undefined || r.affiliate_finance === null) {
				const g = Number(state.user.group);
				return g === 1 || g === 2 || g === 99;
			}
			return Number(r.affiliate_finance) > 0 || Number(state.user.group) === 99;
		} catch {
			return false;
		}
	},
	canWriteAffiliateFinance(state) {
		try {
			const g = Number(state.user.group);
			if (g === 99) return true;
			const r = state.user.roles || {};
			if (r.affiliate_finance === undefined || r.affiliate_finance === null) return g === 1 || g === 2;
			return Number(r.affiliate_finance) >= 2;
		} catch {
			return false;
		}
	},

  isAccessCoupon(state) {
    try {
      return state.user.roles.coupon > 0;
    } catch {
      return false;
    }
	},

	isAccessPO(state) {
    try {
      return state.user.roles.po > 0;
    } catch {
      return false;
    }
	},

	isAccessPayment(state) {
		return state.user.roles.payment > 0;
	},

	isAccessPlatformStatement(state) {
		return state.user.roles.platform_statement > 0;
	},

	isAccessProduct(state) {
		return state.user.roles.product > 0;
	},

	isAccessPacking(state) {
		return state.user.roles.packing > 0;
	},

	isAccessPackingAdmin(state) {
		return state.user.roles.packing_admin > 0;
	},

	isAccessReport(state) {
		return state.user.roles.report > 0;
	},

	isAccessDelivery(state) {
		return state.user.roles.delivery > 0;
	},

	isAccessPlatform(state) {
		return state.user.roles.paltfrom > 0;
	},

	isAccessCustomer(state) {
		return state.user.roles.customer > 0;
	},

	isAccessExpress(state) {
		return state.user.roles.express > 0;
	},

	isAccessOrderReturn(state){
		return state.user.roles.order_return > 0;
	},

	isAccessAccount(state){
		return state.user.roles.account > 0;
	},

	isAccessDashboardByPlatformChannel(state){
		try {
			return state.user.roles.dashboard_by_platform_channel > 0 || state.user.group == 99;
		} catch {
			return state.user.group == 99;
		}
	},

	// Coupon Member permissions (Read=1, Write=2, Delete=4, All=7)
	isReadCouponMember(state) {
		try {
			return (state.user.roles.coupon_member & 1) > 0 || state.user.group == 99;
		} catch {
			return state.user.group == 99;
		}
	},
	isWriteCouponMember(state) {
		try {
			return (state.user.roles.coupon_member & 2) > 0 || state.user.group == 99;
		} catch {
			return state.user.group == 99;
		}
	},
	isDeleteCouponMember(state) {
		try {
			return (state.user.roles.coupon_member & 4) > 0 || state.user.group == 99;
		} catch {
			return state.user.group == 99;
		}
	},

	isAdmin(state) {
		return state.user.group == 1 || state.user.group == 99;
	},

	isOwner(state) {
		return state.user.group == 2;
	},

	isEmployee(state) {
		return state.user.group == 3;
	},

	isAgent(state) {
		return state.user.group == 4;
	},

	isExpress(state) {
		return state.user.group == 5;
	},

	isCustomer(state) {
		return state.user.group == 6;
	},

	isSuperAdmin(state) {
		return state.user.group == 99;
	},

	// MadameFin staff = admin (1), owner (2), employee (3), superadmin (99).
	// Agents (4), express (5), customers (6) and anything else are not.
	// Gates /backoffice/orders-overview (#441, spec 2026-10-09 §B1).
	isMdfStaff(state) {
		const g = Number(state.user.group);
		return g === 1 || g === 2 || g === 3 || g === 99;
	},

	isAccessTiktok(state) {
		return state.user.group == 1 || state.user.group == 99;
	},

	isAccessKOL(state) {
		try {
			return state.user.roles.kol > 0 || state.user.roles.kol_financial > 0 || state.user.group == 99;
		} catch {
			return state.user.group == 99;
		}
	},

	isAccessKOLFinancial(state) {
		try {
			return state.user.roles.kol_financial > 0 || state.user.group == 99;
		} catch {
			return state.user.group == 99;
		}
	},

	isAccessMember(state) {
		try {
			return state.user.roles.member > 0 || state.user.group == 99;
		} catch {
			return state.user.group == 99;
		}
	},

	isEditMember(state) {
		try {
			return state.user.roles.member >= 2 || state.user.group == 99;
		} catch {
			return state.user.group == 99;
		}
	},


	isAccessAgentCommission(state) {
		try {
			return state.user.roles.agent_commission > 0 || state.user.group == 99;
		} catch {
			return state.user.group == 99;
		}
	},

	// Seller area (#46): App Catalog + Storefront + seller Orders + payment report.
	// Since spec 2026-10-16 the permissions come from the login's seller_access
	// (Seller Admin, a grant, or group 99); roles.seller / group 1 only count
	// when the API does not send it (utils/sellerAccessBO.js).
	isSeller(state) {
		return isSellerUser(state.user);
	},

    // isSpecialUser — "may this account see ต้นทุน?".
    //
    // It used to be four hard-coded user ids (1, 3, 3306, 3307), which meant the
    // answer lived in the bundle: it could not be changed without a deploy, it
    // said nothing about who those people are, and it hid the cost column on the
    // FE while GET /api/v2/wms/product still returned Cost to anyone who opened
    // devtools. The server now answers the question at login from config.yml's
    // product_cost.emails (bo-api #594 §4.5) and the ids are gone for good.
    //
    // The NAME is kept on purpose: ProductTables.vue:179/259/265 read it, and
    // renaming the getter in the same change that replaces its meaning would
    // make the diff impossible to review (spec §7.1 / §12 ข้อ 6).
    isSpecialUser(state) {
        return state.user.can_view_cost === true;
    },

    // canViewCost is the same flag under the name the rest of the code should
    // use from here on (router guard, sidebar, the new report screen).
    canViewCost(state) {
        return state.user.can_view_cost === true;
    },

    // canCreateCouponEvent — "may this account create a coupon event?"
    // (coupon-event spec §2/§4). Same shape as canViewCost on purpose: a
    // config-level email allowlist, not a role, so it cannot be handed out from
    // the User screen.
    //
    // This is a UI hint ONLY. It decides whether the button and the form are
    // rendered; the server re-reads config.yml on every request
    // (mw.IsCouponEvent), so a stale localStorage copy can at worst show a form
    // whose POST then answers 403 coupon_event_forbidden.
    canCreateCouponEvent(state) {
        return state.user.can_create_coupon_event === true;
    },

    // Modern Trade permissions (D9): view = role > 0; write = role >= 2; group 99 bypass.
    isAccessModernTrade(state) {
        try {
            return state.user.roles.modern_trade > 0 || state.user.group == 99;
        } catch {
            return state.user.group == 99;
        }
    },
    isEditModernTrade(state) {
        try {
            return state.user.roles.modern_trade >= 2 || state.user.group == 99;
        } catch {
            return state.user.group == 99;
        }
    },

    // Shipping Config permissions: view = role > 0; write = role >= 2; group 99 bypass.
    isAccessShippingConfig(state) {
        try {
            return state.user.roles.shipping_config > 0 || state.user.group == 99;
        } catch {
            return state.user.group == 99;
        }
    },
    isAccessShippingConfigWrite(state) {
        try {
            return state.user.roles.shipping_config >= 2 || state.user.group == 99;
        } catch {
            return state.user.group == 99;
        }
    },

    // POS (#443, spec 2026-10-10 §1.3 / R1): roles.pos is a level — 1 seller,
    // 2 supervisor, 4 admin, compared with >= (7 counts as admin). Group 99
    // always passes. state.user.roles is the merged role JSON from the login
    // body, so Admin/Owner already carry pos = 4 after a re-login.
    isPosSeller(state) {
        try {
            return state.user.roles.pos >= 1 || state.user.group == 99;
        } catch {
            return state.user.group == 99;
        }
    },
    isPosSupervisor(state) {
        try {
            return state.user.roles.pos >= 2 || state.user.group == 99;
        } catch {
            return state.user.group == 99;
        }
    },
    isPosAdmin(state) {
        try {
            return state.user.roles.pos >= 4 || state.user.group == 99;
        } catch {
            return state.user.group == 99;
        }
    },

    // POS phase 3 §13.1: the POS pages of the BO need roles.pos_bo ("เมนู POS
    // ใน BO"), separately from the app level above. A roles JSON without the
    // key (legacy) counts as pos_bo = (pos >= 2). Group 99 always passes.
    // The level still decides what they may do there.
    isPosBO(state) {
        return posBOAccess(state.user).bo;
    },
    isPosBOSupervisor(state) {
        const a = posBOAccess(state.user);
        return a.bo && a.level >= 2;
    },
    isPosBOAdmin(state) {
        const a = posBOAccess(state.user);
        return a.bo && a.level >= 4;
    }




	// Access By Role
	// wms
	// order : {read , write , all}
	// packing { packing,round}




  }
};

export default AuthModule;
